import { API_BASE_URL } from '../config/apiConfig';

export const apiService = {
  // Add a simple cache for search results
  _searchCache: {},

  async saveStory(storyData) {
    console.log('Attempting to save story with data:', storyData);
    try {
      const response = await fetch(`${API_BASE_URL}/api/story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(storyData),
      });
      if (!response.ok) {
        throw new Error('Failed to save the story. Response status: ' + response.status);
      }
      console.log('Story saving request sent');
      return response;
    } catch (error) {
      console.error('Error saving the story:', error);
      throw error;
    }
  },

  async getPrompts() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prompts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch prompts. Response status: ' + response.status);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching prompts:', error);
      throw error;
    }
  },

  // New method to fetch prompt details by ID
  async getPromptDetails(promptId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/prompts/${promptId}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch prompt details');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching prompt details:', error);
      throw error;
    }
  },

  async getEvidence(topic) {
    try {
      // Make topic parameter optional
      const url = topic
        ? `${API_BASE_URL}/api/evidence?topic=${encodeURIComponent(topic)}`
        : `${API_BASE_URL}/api/evidence`;
        
      const response = await fetch(url, { 
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch evidence. Response status: ' + response.status);
      }
  
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching evidence:', error);
      throw error;
    }
  },
  

  async getevidenceDetails(evidenceId) {
    // This function fetches the details of a specific piece of evidence by its ID.
    try {
      // Make a GET request to the API endpoint for evidence details, using the provided evidenceId.
      const response = await fetch(`${API_BASE_URL}/api/evidence/${evidenceId}`, {
        method: 'GET', // Specify the HTTP method as GET.
        headers: {
          'Content-Type': 'application/json', // Set the content type to JSON.
        },
      });

      // Check if the response is not OK (status code is not in the range 200-299).
      if (!response.ok) {
        // If the response is not OK, throw an error with the response status.
        throw new Error('Failed to fetch evidence details. Response status: ' + response.status);
      }

      // Parse the JSON response to extract the evidence details.
      const data = await response.json();
      
      // Return the parsed data, which contains the details of the evidence.
      return data; 
    } catch (error) {
      // If an error occurs during the fetch or parsing, log the error message.
      console.error('Error fetching evidence details:', error);
      
      // Rethrow the error for further handling by the calling function.
      throw error;
    }
  },

  async searchImages(query) {
    const apiKey = process.env.GOOGLE_API_KEY; // Access the environment variable
    const searchEngineId = 'c5e5e0d86c2304c3a'; // Replace with your Custom Search Engine ID

    try {
      const response = await fetch(`https://www.googleapis.com/customsearch/v1?q=${encodeURIComponent(query)}&cx=${searchEngineId}&searchType=image&key=${apiKey}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch images. Response status: ' + response.status);
      }
      const data = await response.json();
      return data.items; // Adjust based on the API response structure
    } catch (error) {
      console.error('Error fetching images:', error);
      throw error;
    }
  },

  async searchGifs(query) {
    const apiKey = process.env.GIPHY_API_KEY; // Access the Giphy API key from the environment variable

    try {
      const response = await fetch(`https://api.giphy.com/v1/gifs/search?q=${encodeURIComponent(query)}&api_key=${apiKey}&limit=25`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch GIFs. Response status: ' + response.status);
      }
      const data = await response.json();
      return data.data; // Return the array of GIF objects
    } catch (error) {
      console.error('Error fetching GIFs:', error);
      throw error;
    }
  },

  async searchEvidence(query, options = {}) {
    try {
      if (!query) {
        console.warn('Empty query provided to searchEvidence');
        return [];
      }

      console.log('API Service searching with query:', query);

      // Get search options with defaults
      const {
        searchType = 'text',
        limit = 100,
        useCache = true,
        bigQuestion = undefined // Only send if explicitly provided
      } = options;

      // Generate cache key if using cache
      const cacheKey = `evidence-${searchType}-${query}`;

      // Check cache first if enabled
      if (useCache && this._searchCache[cacheKey]) {
        console.log('Using cached evidence results for:', query);
        return this._searchCache[cacheKey];
      }

      // Build the search URL with appropriate parameters
      const searchParams = new URLSearchParams({
        query: query,
        searchType: searchType,
        limit: limit
      });
      // Only add bigQuestion if explicitly provided and different from query
      if (bigQuestion && bigQuestion !== query) {
        searchParams.append('bigQuestion', bigQuestion);
      }

      const url = `${API_BASE_URL}/api/evidence/search?${searchParams.toString()}`;
      console.log('Using search URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        let errorMessage = `Failed to search evidence. Status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage += `. Details: ${JSON.stringify(errorData)}`;
          console.error('Search API error details:', errorData);
        } catch (e) {
          // If error response isn't valid JSON, just use status text
          errorMessage += `. ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Validate response data
      if (!Array.isArray(data)) {
        console.error('API returned non-array response:', data);
        throw new Error('Invalid response format from search API');
      }

      console.log(`API Service received ${data.length} results for "${query}"`);

      // Cache the results if caching is enabled
      if (useCache) {
        this._searchCache[cacheKey] = data;

        // Limit cache size to avoid memory issues
        const cacheKeys = Object.keys(this._searchCache);
        if (cacheKeys.length > 50) {
          // Remove oldest cache entries
          const oldestKeys = cacheKeys.slice(0, 10);
          oldestKeys.forEach(key => delete this._searchCache[key]);
        }
      }

      return data;
    } catch (error) {
      console.error('API Service search error:', error);
      throw error;
    }
  },

  async searchPrompts(query) {
    try {
      console.log('API Service searching prompts with query:', query);
      
      const searchUrl = `${API_BASE_URL}/api/prompts/search?query=${encodeURIComponent(query)}&limit=100`;
      console.log('Using search URL:', searchUrl);
      
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error(`Search request failed with status ${response.status}`);
      }
      
      const result = await response.json();
      
      // Check if the response has the expected structure
      if (result && result.success && Array.isArray(result.data)) {
        return result.data; // Return just the array of prompts
      } else {
        console.error('Unexpected API response format:', result);
        throw new Error('Invalid response format from search API');
      }
    } catch (error) {
      console.error('Error searching prompts:', error);
      throw error;
    }
  },

};
